import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from './Header';
import CardGrid from '../components/CardGrid';
import Seo from '../components/Seo';
import api from '../api/client';
import './Search.css';

export default function Search({ translations }) {
    const [params] = useSearchParams();
    const q = params.get('q') || '';
    const [tab, setTab] = useState('artists');
    const [users, setUsers] = useState([]);
    const [pictures, setPictures] = useState([]);
    const [loading, setLoading] = useState(false);
    const t = (key, fallback) => translations?.[key] || fallback;

    const run = useCallback(async () => {
        if (!q.trim()) { setUsers([]); setPictures([]); return; }
        setLoading(true);
        try {
            const [uRes, pRes] = await Promise.all([
                api.searchUsersPaginated(q, 0, 25).catch(() => ({ items: [] })),
                api.searchPictures(q, undefined, 0, 30).catch(() => []),
            ]);
            setUsers(uRes?.items || []);
            setPictures(Array.isArray(pRes) ? pRes : []);
        } finally {
            setLoading(false);
        }
    }, [q]);

    useEffect(() => { run(); }, [run]);

    return (
        <>
            <Seo
                title={q ? `Search: ${q}` : 'Search'}
                description={q
                    ? `Search results for "${q}" — find artists and original artworks on ArtSyde.`
                    : 'Search artists and original artworks on ArtSyde.'}
                noindex={!q}
            />
            <Header translations={translations} />
            <div className="search-page">
                <h1 className="search-heading">
                    {q ? `${t('search-results-for', 'Results for')} "${q}"` : t('search-prompt', 'Search ArtSyde')}
                </h1>
                <div className="search-tabs">
                    <button className={`search-tab ${tab === 'artists' ? 'active' : ''}`} onClick={() => setTab('artists')}>
                        {t('search-artists', 'Artists')} ({users.length})
                    </button>
                    <button className={`search-tab ${tab === 'artworks' ? 'active' : ''}`} onClick={() => setTab('artworks')}>
                        {t('search-artworks', 'Artworks')} ({pictures.length})
                    </button>
                </div>

                {loading ? (
                    <div className="loading-page"><div className="spinner" /></div>
                ) : tab === 'artists' ? (
                    users.length === 0 ? (
                        <p className="search-empty">{t('search-no-artists', 'No artists found.')}</p>
                    ) : (
                        <ul className="search-user-list">
                            {users.map((u) => (
                                <li key={u.publicId}>
                                    <Link to={`/user/${u.publicId}`} className="search-user-item">
                                        {u.profilePicture ? (
                                            <img src={u.profilePicture} alt="" className="search-user-avatar" />
                                        ) : (
                                            <span className="search-user-avatar fallback">
                                                {(u.username || '?')[0].toUpperCase()}
                                            </span>
                                        )}
                                        <div className="search-user-info">
                                            <span className="search-user-name">{u.username || 'Unknown'}</span>
                                            {u.description && <span className="search-user-desc">{u.description}</span>}
                                        </div>
                                        {u.isFollowing && <span className="search-user-tag">{t('profile-following-tag', 'Following')}</span>}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )
                ) : (
                    <CardGrid pictures={pictures} />
                )}
            </div>
        </>
    );
}
